/**
 * 放送地点に紐づくコンテンツ (`BroadcastContent`) のカテゴリ定義と
 * 正規化／初期値ヘルパーをまとめたモジュール。
 *
 * - カテゴリ毎の表示名・必要プロパティ・デフォルト値を 1 箇所に集約しておくことで、
 *   フォーム / バリデーション / Import 時の型変換すべてで同じメタデータを再利用できる。
 * - `BroadcastPoint` のレガシーフィールド (`delay_threshold_seconds` トップレベル)
 *   を読み込んだ場合は、自動的に「自列車遅延」コンテンツに変換する。
 */
import type {
  BroadcastCondition,
  BroadcastContent,
  BroadcastContentCategory,
  BroadcastPoint,
  ConnectingDirectionEntry,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  ConnectingLineEntry,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  FreeTextProperties,
  SelfTrainDelayProperties,
} from '../api/types';

export interface CategoryMeta {
  category: BroadcastContentCategory;
  /** 画面表示用ラベル */
  label: string;
  /** 短い説明 (フォーム説明文用) */
  description: string;
  /** カテゴリ既定値の properties オブジェクトを返す */
  defaultProperties: () => BroadcastContent['properties'];
}

/**
 * 各放送粒度に対応するデフォルト SSML テンプレート文言 (自列車遅延用)。
 * UI の「放送パターン」欄の placeholder / 表示に使用する。
 *
 * 自列車遅延では「ご乗車の電車は」を主語とする従来文面を維持する
 * (運用上、自列車側で線名を読み上げる必要性が低いため)。
 * カスタムテンプレートで自路線の一般的な線名を読みたい場合は ``{route_name}``
 * プレースホルダを使えるよう Lambda 側で展開対応している (route_name 対応)。
 *
 * 既存ユーザーの設定値は ``properties.delay_patterns[].ssml_template`` に個別保存
 * されており、本デフォルトは「未指定時の placeholder / フォールバック」にのみ使われる
 * ため、後方互換は壊れない。
 */
export const GRANULARITY_DEFAULT_SSML: Record<DelayBroadcastGranularity, string> = {
  '1分単位': 'ご乗車の電車は現在、{delay_minutes_1}分遅れで走行しています。',
  '5分単位': 'ご乗車の電車は現在、約{delay_minutes_5}分遅れで走行しています。',
  '10分単位': 'ご乗車の電車は現在、約{delay_minutes_10}分遅れで走行しています。',
  大幅な遅延: 'ご乗車の電車は現在、大幅な遅れで走行しています。',
};

/**
 * 接続線遅延 (transfer_aware モード) 用のデフォルト SSML テンプレート文言。
 * 表形式 UI の「放送パターン」欄 placeholder に表示し、Lambda 側の
 * ``CONNECTING_GRANULARITY_DEFAULT_TEMPLATE`` (放送時の既定文面) と一致させる。
 *
 * - 文面前半は「{from_prefix}お乗換の{route_name}{direction_name}は、」で固定。
 *   ``{from_prefix}`` は Lambda 側で ``from_station`` (= 乗換駅) があれば
 *   「<駅名>駅から」、無ければ空文字に出し分けされる派生プレースホルダ。
 *   transfer_aware モードでは ``transfer_stop_id`` が必須のため、実放送では
 *   常に「<駅名>駅から」となる (例: 「新宿駅からお乗換の…」)。
 *   ``{route_name}`` (一般線名) / ``{direction_name}`` (案内文面用方面文言・
 *   「方面行き」結合済み) も Lambda 側で展開される。
 * - 後半の粒度別分数表現:
 *   - 1分単位: 「現在 {delay_minutes_1} 分遅れています」(切り捨て分数のため「ほど」は付けない)
 *   - 5分単位 / 10分単位: 「現在 {delay_minutes_N} 分ほど遅れています」(切り捨てでざっくり読み上げ)
 *   - 大幅な遅延: 「現在大幅な遅れがでています」(分数を読み上げない)
 *
 * Lambda 側 (``lambda/update-contents/index.py`` の
 * ``CONNECTING_GRANULARITY_DEFAULT_TEMPLATE``) と文字列レベルで一致させているため、
 * デフォルト文面の挙動を変更する際は両方を同時に更新する必要がある。
 */
export const CONNECTING_GRANULARITY_DEFAULT_SSML: Record<DelayBroadcastGranularity, string> = {
  '1分単位':
    '{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_1}分遅れています。',
  '5分単位':
    '{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_5}分ほど遅れています。',
  '10分単位':
    '{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_10}分ほど遅れています。',
  大幅な遅延: '{from_prefix}お乗換の{route_name}{direction_name}は、現在大幅な遅れがでています。',
};

/** 放送粒度の選択肢一覧 */
export const DELAY_BROADCAST_GRANULARITIES: readonly DelayBroadcastGranularity[] = [
  '1分単位',
  '5分単位',
  '10分単位',
  '大幅な遅延',
];

/**
 * 自列車遅延コンテンツのデフォルト遅延放送パターン (新規作成時の初期値)。
 * Q1 回答: 1分以上〜10分未満 / 10分以上〜30分未満 / 30分以上〜60分未満 / 60分以上
 *
 * 各行の ``ssml_template`` には粒度別デフォルト文 (`GRANULARITY_DEFAULT_SSML`) を
 * **実値として** 設定する。これまで placeholder (薄文字) で表示していたデフォルト文を
 * 初期値として書き込み、ユーザは編集する場合はゼロベースで書き直す方針。
 */
const DEFAULT_DELAY_PATTERNS: readonly DelayBroadcastPattern[] = [
  {
    delay_minutes_from: 1,
    delay_minutes_to: 10,
    granularity: '1分単位',
    ssml_template: GRANULARITY_DEFAULT_SSML['1分単位'],
  },
  {
    delay_minutes_from: 10,
    delay_minutes_to: 30,
    granularity: '5分単位',
    ssml_template: GRANULARITY_DEFAULT_SSML['5分単位'],
  },
  {
    delay_minutes_from: 30,
    delay_minutes_to: 60,
    granularity: '10分単位',
    ssml_template: GRANULARITY_DEFAULT_SSML['10分単位'],
  },
  {
    delay_minutes_from: 60,
    granularity: '大幅な遅延',
    ssml_template: GRANULARITY_DEFAULT_SSML['大幅な遅延'],
  },
];

/**
 * 接続線遅延コンテンツ (transfer_aware モード) のデフォルト遅延放送パターン。
 *
 * 自列車遅延と同じ「遅れ分数 → 放送粒度」の対応で初期値を構成する。
 * 判定モードを `transfer_aware` に切り替えたタイミングで `delay_patterns` 未設定なら
 * このパターンを初期値として注入する。
 *
 * 各行の ``ssml_template`` には粒度別デフォルト文 (`CONNECTING_GRANULARITY_DEFAULT_SSML`)
 * を **実値として** 設定する。
 */
export const DEFAULT_CONNECTING_DELAY_PATTERNS: readonly DelayBroadcastPattern[] = [
  {
    delay_minutes_from: 1,
    delay_minutes_to: 10,
    granularity: '1分単位',
    ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML['1分単位'],
  },
  {
    delay_minutes_from: 10,
    delay_minutes_to: 30,
    granularity: '5分単位',
    ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML['5分単位'],
  },
  {
    delay_minutes_from: 30,
    delay_minutes_to: 60,
    granularity: '10分単位',
    ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML['10分単位'],
  },
  {
    delay_minutes_from: 60,
    granularity: '大幅な遅延',
    ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML['大幅な遅延'],
  },
];

/**
 * 接続線遅延コンテンツ (whole_line / section_scheduled モード) の
 * 単一 SSML テンプレートのデフォルト文言。
 *
 * これまで <textarea> の placeholder で表示していた文言を、新規作成時の初期値として
 * 実際に `ssml_template` に書き込む。`whole_line` モードでは Lambda 側で
 * `{from_prefix}` / `{from_station}` / `{direction_name}` が空文字に強制展開されるため、
 * これらを含まない短い文面を採用する。
 */
export const CONNECTING_SINGLE_SSML_DEFAULT: Record<'whole_line' | 'section_scheduled', string> = {
  whole_line: '<speak>お乗換の{route_name}は、現在遅れて運行しています</speak>',
  section_scheduled:
    '<speak>{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_1}分ほど遅れて運行しています。</speak>',
};

// ---------------------------------------------------------------------------
// 接続線遅延 (乗換駅ベース / part2 / `connecting_line_delay_by_station`) の
// デフォルト SSML テンプレート・デフォルトパターン。
// ---------------------------------------------------------------------------

/**
 * 接続線遅延 (乗換駅ベース / part2) の **冒頭 SSML テンプレート** のデフォルト文言。
 *
 * ``{transfer_station}`` プレースホルダは Lambda 側で乗換駅名に展開される
 * (例: 「赤羽」)。UI の <input> 入力欄の placeholder / 新規作成時の初期値としても使う。
 *
 * Lambda 側 (``lambda/update-contents/index.py`` の
 * ``DEFAULT_CONNECTING_STATION_INTRO_TEMPLATE``) と文字列レベルで一致させる。
 */
export const CONNECTING_STATION_INTRO_DEFAULT_SSML = '{transfer_station}駅からお乗換の';

/**
 * 接続線遅延 (乗換駅ベース / part2) の **接続線ブロック SSML テンプレート** のデフォルト文言。
 *
 * ``{route_name}`` は路線の一般名称に、``{directions_text}`` は方面部分文
 * (part2 の各 ``ConnectingDirectionEntry`` を read out するために ``delay_patterns[i].ssml_template``
 * から組み立てた文字列) を読点連結した結果に、Lambda 側で展開される。
 *
 * 「〜は、」の「は」を含めていないのは、方面部分文の冒頭で「〜方面は」と表現するため、
 * 二重「は」を回避する意図。
 *
 * Lambda 側 (``DEFAULT_CONNECTING_STATION_LINE_TEMPLATE``) と一致させる。
 */
export const CONNECTING_STATION_LINE_DEFAULT_SSML =
  '{route_name}、{directions_text}となっております。';

/**
 * 接続線遅延 (乗換駅ベース / part2) の **方面部分文 SSML テンプレート** のデフォルト文言。
 *
 * 表形式 UI の各行 (=遅延放送パターン) にひも付き、Lambda 側でこの文言を各方面ぶん
 * 展開してから読点で連結し、``{directions_text}`` プレースホルダに埋め込む。
 *
 * 使えるプレースホルダ:
 * - ``{direction_name}`` (方面名、必須。空だと放送文が破綻するのでバリデーションで担保)
 * - ``{delay_minutes_1/5/10}`` / ``{delay_minutes}`` / ``{delay_seconds}`` / ``{delay}``
 * - ``{route_name}`` / ``{route_id}`` / ``{route}``
 * - ``{transfer_station}``
 *
 * Lambda 側 (``CONNECTING_STATION_GRANULARITY_DEFAULT_TEMPLATE``) と一致させる。
 */
export const CONNECTING_STATION_GRANULARITY_DEFAULT_SSML: Record<
  DelayBroadcastGranularity,
  string
> = {
  '1分単位': '{direction_name}方面は{delay_minutes_1}分遅れ',
  '5分単位': '{direction_name}方面は約{delay_minutes_5}分遅れ',
  '10分単位': '{direction_name}方面は約{delay_minutes_10}分遅れ',
  大幅な遅延: '{direction_name}方面は大幅な遅れ',
};

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ぶんのデフォルト遅延放送パターン。
 *
 * part1 の ``DEFAULT_CONNECTING_DELAY_PATTERNS`` と同じ「遅れ分数 → 放送粒度」の対応で
 * 初期値を構成する。接続線ブロックを新規追加した際にこのパターンを注入する。
 *
 * 各行の ``ssml_template`` は **方面部分文** (`{directions_text}` に流し込まれる素材) を
 * 表す点が part1 と違うので、専用のデフォルト文言を持つ (`CONNECTING_STATION_GRANULARITY_DEFAULT_SSML`)。
 */
export const DEFAULT_CONNECTING_STATION_DELAY_PATTERNS: readonly DelayBroadcastPattern[] = [
  {
    delay_minutes_from: 1,
    delay_minutes_to: 10,
    granularity: '1分単位',
    ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML['1分単位'],
  },
  {
    delay_minutes_from: 10,
    delay_minutes_to: 30,
    granularity: '5分単位',
    ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML['5分単位'],
  },
  {
    delay_minutes_from: 30,
    delay_minutes_to: 60,
    granularity: '10分単位',
    ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML['10分単位'],
  },
  {
    delay_minutes_from: 60,
    granularity: '大幅な遅延',
    ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML['大幅な遅延'],
  },
];

/**
 * 放送粒度に対応するデフォルト方面部分文 SSML を返すヘルパー (接続線遅延 part2 用)。
 * 粒度プルダウンを変更したタイミングで、対応する粒度のデフォルト文に
 * ssml_template を上書きする目的で使用する (自列車遅延 / part1 と同様の運用)。
 */
export function getDefaultConnectingStationSsml(granularity: DelayBroadcastGranularity): string {
  return CONNECTING_STATION_GRANULARITY_DEFAULT_SSML[granularity];
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の「新規接続線ブロック」既定値を生成する。
 * ＋ボタンで接続線を追加したときに使う。
 */
export function createDefaultConnectingStationLine(): ConnectingLineEntry {
  return {
    route_id: '',
    delay_judgement_mode: 'whole_line',
    transfer_time_seconds: 180,
    ssml_line_template: CONNECTING_STATION_LINE_DEFAULT_SSML,
    delay_patterns: DEFAULT_CONNECTING_STATION_DELAY_PATTERNS.map((p) => ({ ...p })),
    directions: [createDefaultConnectingStationDirection()],
  };
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の「新規方面」既定値を生成する。
 * ＋ボタンで方面を追加したときに使う。
 */
export function createDefaultConnectingStationDirection(): ConnectingDirectionEntry {
  return {
    direction_id: '',
    direction_name: '',
  };
}

/**
 * 放送粒度に対応するデフォルト SSML 文言を返すヘルパー (自列車遅延用)。
 * 粒度プルダウンを変更したタイミングで、対応する粒度のデフォルト文に
 * ssml_template を上書きする目的で使用する。
 */
export function getDefaultSelfTrainSsml(granularity: DelayBroadcastGranularity): string {
  return GRANULARITY_DEFAULT_SSML[granularity];
}

/**
 * 放送粒度に対応するデフォルト SSML 文言を返すヘルパー (接続線 transfer_aware 用)。
 */
export function getDefaultConnectingSsml(granularity: DelayBroadcastGranularity): string {
  return CONNECTING_GRANULARITY_DEFAULT_SSML[granularity];
}

export const CATEGORY_META: readonly CategoryMeta[] = [
  {
    category: 'self_train_delay',
    label: '自列車遅延',
    description: '自列車の遅延状況に応じて、遅れ分数の幅ごとに放送パターンを設定します。',
    defaultProperties: (): SelfTrainDelayProperties => ({
      delay_patterns: DEFAULT_DELAY_PATTERNS.map((p) => ({ ...p })),
    }),
  },
  {
    category: 'connecting_line_delay',
    label: '接続線遅延',
    description:
      '指定した接続線 (route_id) が遅延しているときに放送します。判定モードに応じて、接続線全体の遅延 / 区間・在線便の遅延 / 乗り換え列車の遅延 を切り替えられます。',
    defaultProperties: (): ConnectingLineDelayProperties => ({
      route_id: '',
      delay_threshold_seconds: 60,
      // 既定の判定モードは「接続線全体の遅延」(whole_line / 条件1)。
      // whole_line は「接続線全体の最大遅延」のみで判定する純化されたモードのため、
      // 区間 (start/end_stop_id) / 乗換駅 (transfer_stop_id) / 方面 (direction_id) は
      // 既定値に含めない (他モードに切り替わったタイミングで初めて入力する)。
      // これにより新規作成 → そのまま保存した際に DB に未使用フィールドが残らない。
      delay_judgement_mode: 'whole_line',
      transfer_time_seconds: 180,
      // 単一 SSML テンプレートのデフォルト文言 (whole_line 用)。
      // これまで placeholder (薄文字) で表示していた文言を、新規作成時の初期値として
      // 実値で書き込む。判定モードを section_scheduled に切り替えた際は UI 側で
      // section_scheduled 用のデフォルト文に上書きする。
      ssml_template: CONNECTING_SINGLE_SSML_DEFAULT.whole_line,
    }),
  },
  {
    category: 'connecting_line_delay_by_station',
    // UI 表示上のラベル。以前は「接続線遅延（乗換駅別）」だったが、旧カテゴリ
    // `connecting_line_delay` (乗換駅別ではない旧仕様) を新規作成ドロップダウンから
    // 外した結果、ユーザ向けには実質「唯一の接続線遅延カテゴリ」となったため、
    // 括弧書きを外して単に「接続線遅延」と表示する。
    // ※ サマリ表示 (`BroadcastPointsTable.resolveContentSummary`) では
    //    「接続線遅延（乗換駅: ... / 接続線 N件）」形式となり、旧カテゴリの
    //    「接続線遅延（route_id: ... / しきい値 ...秒）」形式と括弧内で区別できる。
    label: '接続線遅延',
    description:
      '乗換駅を軸に、複数の接続線 (各1〜2方面) の遅延をまとめて案内するコンテンツです。 例: 「赤羽駅からお乗換の埼京線、新宿方面は5分遅れ、大宮方面は3分遅れとなっております。」',
    defaultProperties: (): ConnectingLineDelayByStationProperties => ({
      transfer_stop_id: '',
      ssml_intro_template: CONNECTING_STATION_INTRO_DEFAULT_SSML,
      lines: [createDefaultConnectingStationLine()],
    }),
  },

  {
    category: 'free_text',
    label: '任意テキストの放送',
    description: '指定した SSML を放送します (`<speak>...</speak>` を含めてください)。',
    defaultProperties: (): FreeTextProperties => ({
      ssml: '<speak></speak>',
    }),
  },
];

const CATEGORY_META_BY_KEY: Record<BroadcastContentCategory, CategoryMeta> = Object.freeze(
  Object.fromEntries(CATEGORY_META.map((m) => [m.category, m])) as Record<
    BroadcastContentCategory,
    CategoryMeta
  >,
);

/** カテゴリ識別子からメタデータを取得する。未知カテゴリは undefined。 */
export function getCategoryMeta(category: string): CategoryMeta | undefined {
  return CATEGORY_META_BY_KEY[category as BroadcastContentCategory];
}

/**
 * コンテンツ種別ごとの薄い背景色 (カード色分け用)。
 * - 自列車遅延: 薄い赤
 * - 接続線遅延 (part1 / part2 どちらも): 薄い青
 * - 任意テキスト: 薄い緑
 * 未知カテゴリはニュートラルなグレーにフォールバックする。
 */
const CATEGORY_BACKGROUND_COLORS: Record<BroadcastContentCategory, string> = {
  self_train_delay: '#fdecec',
  connecting_line_delay: '#e8f1fb',
  connecting_line_delay_by_station: '#e8f1fb',
  free_text: '#e9f5ec',
};

/** コンテンツ種別に対応する薄い背景色を返す (カードの色分けに使う)。 */
export function getCategoryColor(category: string): string {
  return CATEGORY_BACKGROUND_COLORS[category as BroadcastContentCategory] ?? '#f1f3f5';
}

/** カテゴリの初期コンテンツを生成する */
export function createDefaultContent(category: BroadcastContentCategory): BroadcastContent {
  const meta = CATEGORY_META_BY_KEY[category];
  return {
    category,
    properties: meta.defaultProperties(),
  };
}

/**
 * BroadcastPoint から「フォームで扱うコンテンツ配列」を取り出す。
 *
 * - `contents` フィールドがあればそれを優先する (深いコピーを返す)。
 * - 無い場合は、レガシーフィールド `delay_threshold_seconds` を「自列車遅延」コンテンツへ
 *   変換した 1 件を返す (Issue #56 互換)。
 * - レガシーフィールドも無い場合は空配列を返す。
 */
export function extractContents(
  point: Pick<BroadcastPoint, 'contents' | 'delay_threshold_seconds'>,
): BroadcastContent[] {
  if (Array.isArray(point.contents) && point.contents.length > 0) {
    return point.contents.map((c) =>
      backfillDefaultSsmlTemplates(normalizeSelfTrainDelayContent(cloneContent(c))),
    );
  }
  if (typeof point.delay_threshold_seconds === 'number') {
    // レガシー: delay_threshold_seconds を新形式 delay_patterns 1行に変換する。
    // 閾値秒数を分単位に変換し (切り上げ)、「1分単位」で放送するパターンとして扱う。
    const fromMinutes = Math.ceil(point.delay_threshold_seconds / 60);
    return [
      backfillDefaultSsmlTemplates({
        category: 'self_train_delay',
        properties: {
          delay_patterns: [{ delay_minutes_from: fromMinutes, granularity: '1分単位' }],
        } satisfies SelfTrainDelayProperties,
      }),
    ];
  }
  return [];
}

/**
 * 既存データの `ssml_template` が未設定 (undefined / 空文字) の場合に、
 * 各 SSML 入力欄のデフォルト文言を **実値として補完** する。
 *
 * これまで「未設定なら Lambda 側でデフォルト文を使う」という後方互換に頼って
 * DB に `ssml_template` が無いままのレコードが多数存在する。SSML 入力欄を
 * 「初期値プリセット + 必須」に変更したため、編集画面で開いた際に空欄として
 * 見えると「保存できない」状態になってしまう。これを避けるため、読み込み時に
 * 該当カテゴリ・モードに応じたデフォルト文をフォーム上に展開する。
 *
 * - 自列車遅延 (`self_train_delay`): 各 `delay_patterns[i]` の粒度に応じた
 *   `GRANULARITY_DEFAULT_SSML` で補完。
 * - 接続線遅延 (`connecting_line_delay`):
 *   - `transfer_aware`: 各 `delay_patterns[i]` の粒度に応じた
 *     `CONNECTING_GRANULARITY_DEFAULT_SSML` で補完。
 *   - `whole_line` / `section_scheduled`: 単一 `ssml_template` を
 *     `CONNECTING_SINGLE_SSML_DEFAULT[mode]` で補完。
 * - その他 (`free_text` 等) は対象外。
 */
function backfillDefaultSsmlTemplates(content: BroadcastContent): BroadcastContent {
  if (content.category === 'self_train_delay') {
    const props = content.properties as Partial<SelfTrainDelayProperties>;
    if (!Array.isArray(props.delay_patterns)) return content;
    const nextPatterns = props.delay_patterns.map((p) => {
      const granularity = p.granularity ?? '1分単位';
      const current = typeof p.ssml_template === 'string' ? p.ssml_template : '';
      if (current.trim() === '') {
        return { ...p, ssml_template: GRANULARITY_DEFAULT_SSML[granularity] };
      }
      return p;
    });
    // schedule は SSML バックフィルとは独立した任意プロパティ。
    // 新しい content オブジェクトを構築する際に必ず引き継ぐこと (回帰防止: 引き継ぎ忘れると
    // 編集画面リロード時にスケジュール設定が消えて見える現象になる)。
    return {
      category: 'self_train_delay',
      properties: { ...props, delay_patterns: nextPatterns } as SelfTrainDelayProperties,
      ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
    };
  }
  if (content.category === 'connecting_line_delay') {
    const props = content.properties as Partial<ConnectingLineDelayProperties>;
    const mode = props.delay_judgement_mode ?? 'whole_line';
    const next: Partial<ConnectingLineDelayProperties> = { ...props };
    if (mode === 'transfer_aware') {
      // transfer_aware モードは delay_patterns 側の ssml_template を補完する。
      // 単一 ssml_template (whole_line / section_scheduled 用) は使用しないので触らない。
      if (Array.isArray(props.delay_patterns)) {
        next.delay_patterns = props.delay_patterns.map((p) => {
          const granularity = p.granularity ?? '1分単位';
          const current = typeof p.ssml_template === 'string' ? p.ssml_template : '';
          if (current.trim() === '') {
            return { ...p, ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML[granularity] };
          }
          return p;
        });
      }
    } else {
      // whole_line / section_scheduled モードは単一 ssml_template を補完する。
      const current = typeof props.ssml_template === 'string' ? props.ssml_template : '';
      if (current.trim() === '') {
        next.ssml_template = CONNECTING_SINGLE_SSML_DEFAULT[mode];
      }
    }
    return {
      category: 'connecting_line_delay',
      properties: next as ConnectingLineDelayProperties,
      // schedule は必ず引き継ぐ (詳細は self_train_delay 分岐のコメント参照)
      ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
    };
  }
  if (content.category === 'connecting_line_delay_by_station') {
    // 接続線遅延 (乗換駅ベース / part2) の SSML デフォルト補完。
    //
    // 3層のテンプレート (intro / line / direction=delay_patterns[i].ssml_template)
    // それぞれに対してデフォルト文言を補完する。
    // - intro (`ssml_intro_template`): `CONNECTING_STATION_INTRO_DEFAULT_SSML`
    // - line (`lines[j].ssml_line_template`): `CONNECTING_STATION_LINE_DEFAULT_SSML`
    // - direction (`lines[j].delay_patterns[k].ssml_template`):
    //   粒度に応じた `CONNECTING_STATION_GRANULARITY_DEFAULT_SSML[粒度]`
    //
    // ※ 判定モード別分岐 (part1 のような whole_line/section_scheduled/transfer_aware) は
    //   不要。part2 は「遅延判定は方面ごとに 3 モード分岐、放送文組み立ては共通」の設計。
    const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
    const introCurrent =
      typeof props.ssml_intro_template === 'string' ? props.ssml_intro_template : '';
    const nextIntro =
      introCurrent.trim() === ''
        ? CONNECTING_STATION_INTRO_DEFAULT_SSML
        : props.ssml_intro_template;
    const nextLines = Array.isArray(props.lines)
      ? props.lines.map((line) => {
          const lineCurrent =
            typeof line.ssml_line_template === 'string' ? line.ssml_line_template : '';
          const nextLineTemplate =
            lineCurrent.trim() === ''
              ? CONNECTING_STATION_LINE_DEFAULT_SSML
              : line.ssml_line_template;
          const nextPatterns = Array.isArray(line.delay_patterns)
            ? line.delay_patterns.map((p) => {
                const granularity = p.granularity ?? '1分単位';
                const current = typeof p.ssml_template === 'string' ? p.ssml_template : '';
                if (current.trim() === '') {
                  return {
                    ...p,
                    ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML[granularity],
                  };
                }
                return p;
              })
            : line.delay_patterns;
          return {
            ...line,
            ssml_line_template: nextLineTemplate,
            delay_patterns: nextPatterns,
          } as ConnectingLineEntry;
        })
      : props.lines;
    return {
      category: 'connecting_line_delay_by_station',
      properties: {
        ...props,
        ssml_intro_template: nextIntro,
        lines: nextLines,
      } as ConnectingLineDelayByStationProperties,
      // schedule は必ず引き継ぐ (詳細は self_train_delay 分岐のコメント参照)
      ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
    };
  }
  return content;
}

/**
 * self_train_delay コンテンツを新形式に正規化する。
 * 旧形式 (delay_threshold_seconds + ssml_template) が含まれていれば
 * delay_patterns 1行に変換する。既に delay_patterns があればそのまま返す。
 */
function normalizeSelfTrainDelayContent(content: BroadcastContent): BroadcastContent {
  if (content.category !== 'self_train_delay') return content;
  const props = content.properties as Record<string, unknown>;
  // 既に新形式なら何もしない
  if (Array.isArray(props.delay_patterns)) return content;
  // 旧形式: delay_threshold_seconds から変換
  const thresholdSec =
    typeof props.delay_threshold_seconds === 'number' ? props.delay_threshold_seconds : 0;
  const fromMinutes = Math.ceil(thresholdSec / 60);
  const pattern: DelayBroadcastPattern = {
    delay_minutes_from: fromMinutes,
    granularity: '1分単位',
  };
  if (typeof props.ssml_template === 'string' && props.ssml_template.trim() !== '') {
    pattern.ssml_template = props.ssml_template;
  }
  return {
    category: 'self_train_delay',
    properties: { delay_patterns: [pattern] } satisfies SelfTrainDelayProperties,
  };
}

/** コンテンツをディープコピーする (immer 等を使わず最低限) */
export function cloneContent(content: BroadcastContent): BroadcastContent {
  const cloned: BroadcastContent = {
    category: content.category,
    properties: { ...(content.properties as Record<string, unknown>) },
  };
  // schedule は任意プロパティ。存在すれば浅くコピー
  // (BroadcastSchedule の各値は string / string[] / TimeRange[] で、内部で mutate しないため
  //  スプレッドコピーで十分。TimeRange[] は API 送信前後で immutable に扱う)
  if (content.schedule !== undefined) {
    cloned.schedule = {
      ...content.schedule,
      days_of_week: content.schedule.days_of_week ? [...content.schedule.days_of_week] : undefined,
      time_ranges: content.schedule.time_ranges
        ? content.schedule.time_ranges.map((r) => ({ ...r }))
        : undefined,
    };
  }
  return cloned;
}

/**
 * unknown を BroadcastContent[] へ正規化する。Import / API レスポンスの後処理用。
 * - 配列でない / オブジェクトでない要素はスキップ。
 * - category が未知の場合もスキップ (logger 出力は呼び出し側で行う)。
 */
export function normalizeContents(value: unknown): BroadcastContent[] {
  if (!Array.isArray(value)) return [];
  const result: BroadcastContent[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;
    const category = obj.category;
    if (typeof category !== 'string') continue;
    if (!getCategoryMeta(category)) continue;
    const props =
      obj.properties && typeof obj.properties === 'object'
        ? (obj.properties as Record<string, unknown>)
        : {};
    const next: BroadcastContent = {
      category: category as BroadcastContentCategory,
      properties: { ...props },
    };
    // schedule は API レスポンス / import データからそのまま受け取る。
    // 詳細な形式検証 (曜日 enum / HH:mm / start<=end) はサーバ側 (`_normalize_schedule`)
    // と UI 側 (`BroadcastScheduleEditor`) で行うため、ここでは object 判定のみ実施。
    if (obj.schedule && typeof obj.schedule === 'object') {
      next.schedule = obj.schedule as BroadcastContent['schedule'];
    }
    result.push(next);
  }
  return result;
}

/** バリデーション。問題があれば日本語エラー文字列を返す (null = 問題なし) */
export function validateContent(content: BroadcastContent, index: number): string | null {
  const prefix = `コンテンツ #${index + 1}`;
  switch (content.category) {
    case 'self_train_delay': {
      const props = content.properties as Partial<SelfTrainDelayProperties>;
      if (!Array.isArray(props.delay_patterns) || props.delay_patterns.length === 0) {
        return `${prefix} (自列車遅延): 遅延放送パターンを 1 件以上設定してください`;
      }
      for (let i = 0; i < props.delay_patterns.length; i++) {
        const pattern = props.delay_patterns[i] as Partial<DelayBroadcastPattern>;
        if (
          typeof pattern.delay_minutes_from !== 'number' ||
          Number.isNaN(pattern.delay_minutes_from) ||
          pattern.delay_minutes_from < 0 ||
          !Number.isInteger(pattern.delay_minutes_from)
        ) {
          return `${prefix} (自列車遅延) パターン${i + 1}: 遅れ開始分数を 0 以上の整数で指定してください`;
        }
        if (pattern.delay_minutes_to !== undefined) {
          if (
            typeof pattern.delay_minutes_to !== 'number' ||
            Number.isNaN(pattern.delay_minutes_to) ||
            !Number.isInteger(pattern.delay_minutes_to) ||
            pattern.delay_minutes_to <= pattern.delay_minutes_from
          ) {
            return `${prefix} (自列車遅延) パターン${i + 1}: 遅れ終了分数は開始分数より大きい整数で指定してください`;
          }
        }
        // SSML 入力欄は必須。空文字 (ユーザがクリアしてゼロベースで入力中の状態) は
        // 保存できないようにバリデーションエラーにする。
        if (typeof pattern.ssml_template !== 'string' || pattern.ssml_template.trim() === '') {
          return `${prefix} (自列車遅延) パターン${i + 1}: 放送パターン (SSMLテンプレート) を入力してください`;
        }
      }
      return null;
    }
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      if (typeof props.route_id !== 'string' || props.route_id.trim() === '') {
        return `${prefix} (接続線遅延): 接続線の route_id を指定してください`;
      }
      if (
        typeof props.delay_threshold_seconds !== 'number' ||
        Number.isNaN(props.delay_threshold_seconds) ||
        props.delay_threshold_seconds < 0
      ) {
        return `${prefix} (接続線遅延): 遅延しきい値秒数を 0 以上で指定してください`;
      }
      // 判定モード別の必須項目チェック。
      const mode = props.delay_judgement_mode ?? 'whole_line';
      if (mode === 'section_scheduled') {
        // 区間・在線便モードは開始駅・終了駅が必須。
        const hasStart =
          typeof props.start_stop_id === 'string' && props.start_stop_id.trim() !== '';
        const hasEnd = typeof props.end_stop_id === 'string' && props.end_stop_id.trim() !== '';
        if (!hasStart || !hasEnd) {
          return `${prefix} (接続線遅延): 「区間・在線便の遅延」モードでは遅延判定開始駅と終了駅を指定してください`;
        }
      }
      if (mode === 'transfer_aware') {
        // 乗換列車モードは乗換駅と乗換時間が必須。
        const hasTransferStop =
          typeof props.transfer_stop_id === 'string' && props.transfer_stop_id.trim() !== '';
        if (!hasTransferStop) {
          return `${prefix} (接続線遅延): 「乗り換え列車の遅延」モードでは乗換駅を指定してください`;
        }
        if (
          typeof props.transfer_time_seconds !== 'number' ||
          Number.isNaN(props.transfer_time_seconds) ||
          props.transfer_time_seconds < 0 ||
          !Number.isInteger(props.transfer_time_seconds)
        ) {
          return `${prefix} (接続線遅延): 「乗り換え列車の遅延」モードでは乗換時間 (秒) を 0 以上の整数で指定してください`;
        }
        // 「乗り換え列車の遅延」モードは遅れ分数の幅ごとに粒度を切り替える表形式で
        // 設定する。delay_patterns が指定されていれば自列車遅延と同じ整合性チェックを行う。
        // delay_patterns 未指定の場合は旧スキーマ (delay_threshold_seconds + 単一 ssml_template)
        // で動作するため、ここではエラーにしない (後方互換)。
        if (props.delay_patterns !== undefined) {
          if (!Array.isArray(props.delay_patterns) || props.delay_patterns.length === 0) {
            return `${prefix} (接続線遅延): 「乗り換え列車の遅延」モードでは遅延放送パターンを 1 件以上設定してください`;
          }
          for (let i = 0; i < props.delay_patterns.length; i++) {
            const pattern = props.delay_patterns[i] as Partial<DelayBroadcastPattern>;
            if (
              typeof pattern.delay_minutes_from !== 'number' ||
              Number.isNaN(pattern.delay_minutes_from) ||
              pattern.delay_minutes_from < 0 ||
              !Number.isInteger(pattern.delay_minutes_from)
            ) {
              return `${prefix} (接続線遅延) パターン${i + 1}: 遅れ開始分数を 0 以上の整数で指定してください`;
            }
            if (pattern.delay_minutes_to !== undefined) {
              if (
                typeof pattern.delay_minutes_to !== 'number' ||
                Number.isNaN(pattern.delay_minutes_to) ||
                !Number.isInteger(pattern.delay_minutes_to) ||
                pattern.delay_minutes_to <= pattern.delay_minutes_from
              ) {
                return `${prefix} (接続線遅延) パターン${i + 1}: 遅れ終了分数は開始分数より大きい整数で指定してください`;
              }
            }
            // 各行の SSML 入力欄は必須 (ゼロベース入力する際は明示的に書く)。
            if (typeof pattern.ssml_template !== 'string' || pattern.ssml_template.trim() === '') {
              return `${prefix} (接続線遅延) パターン${i + 1}: 放送パターン (SSMLテンプレート) を入力してください`;
            }
          }
        }
      } else {
        // whole_line / section_scheduled モードは単一 ssml_template を必須とする
        // (ゼロベースで書き直したい場合も、空欄では保存させない)。
        if (typeof props.ssml_template !== 'string' || props.ssml_template.trim() === '') {
          return `${prefix} (接続線遅延): SSML テンプレートを入力してください`;
        }
      }
      return null;
    }
    case 'connecting_line_delay_by_station': {
      // 接続線遅延 (乗換駅ベース / part2) のバリデーション。
      // 三層構造 (乗換駅コンテンツ → 接続線ブロック → 方面) と、接続線ブロックごとの
      // delay_patterns (遅れ分数の幅 × 放送粒度 × 方面部分文SSML) をすべてチェックする。
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      if (typeof props.transfer_stop_id !== 'string' || props.transfer_stop_id.trim() === '') {
        return `${prefix} (接続線遅延／乗換駅別): 乗換駅を指定してください`;
      }
      if (!Array.isArray(props.lines) || props.lines.length === 0) {
        return `${prefix} (接続線遅延／乗換駅別): 接続線を 1 件以上追加してください`;
      }
      for (let li = 0; li < props.lines.length; li++) {
        const line = props.lines[li] as Partial<ConnectingLineEntry> | undefined;
        const linePrefix = `${prefix} (接続線遅延／乗換駅別) 接続線${li + 1}`;
        if (!line || typeof line !== 'object') {
          return `${linePrefix}: 接続線の設定が不正です`;
        }
        if (typeof line.route_id !== 'string' || line.route_id.trim() === '') {
          return `${linePrefix}: 接続線 (route_id) を指定してください`;
        }
        if (
          line.delay_judgement_mode !== 'whole_line' &&
          line.delay_judgement_mode !== 'section_scheduled' &&
          line.delay_judgement_mode !== 'transfer_aware'
        ) {
          return `${linePrefix}: 判定モードを選択してください`;
        }
        // 判定モードが transfer_aware のときは乗換時間 (秒) が必要。
        if (line.delay_judgement_mode === 'transfer_aware') {
          if (
            typeof line.transfer_time_seconds !== 'number' ||
            Number.isNaN(line.transfer_time_seconds) ||
            line.transfer_time_seconds < 0 ||
            !Number.isInteger(line.transfer_time_seconds)
          ) {
            return `${linePrefix}: 「乗り換え列車の遅延」モードでは乗換時間 (秒) を 0 以上の整数で指定してください`;
          }
        }
        // 方面 (directions) は 1〜2 件必須。
        if (!Array.isArray(line.directions) || line.directions.length === 0) {
          return `${linePrefix}: 方面を 1 件以上追加してください`;
        }
        if (line.directions.length > 2) {
          return `${linePrefix}: 方面は最大 2 件までです`;
        }
        for (let di = 0; di < line.directions.length; di++) {
          const dir = line.directions[di] as Partial<ConnectingDirectionEntry> | undefined;
          const dirPrefix = `${linePrefix} 方面${di + 1}`;
          if (!dir || typeof dir !== 'object') {
            return `${dirPrefix}: 方面の設定が不正です`;
          }
          if (typeof dir.direction_name !== 'string' || dir.direction_name.trim() === '') {
            // 案内文面用の方面名は放送文の主要トークンなので必須。
            return `${dirPrefix}: 案内文面用の方面名 (例:「新宿」) を入力してください`;
          }
          // section_scheduled モードでは方面ごとに区間 (start/end_stop_id) が必須。
          if (line.delay_judgement_mode === 'section_scheduled') {
            const hasStart =
              typeof dir.start_stop_id === 'string' && dir.start_stop_id.trim() !== '';
            const hasEnd = typeof dir.end_stop_id === 'string' && dir.end_stop_id.trim() !== '';
            if (!hasStart || !hasEnd) {
              return `${dirPrefix}: 「区間・在線便の遅延」モードでは遅延判定開始駅と終了駅を指定してください`;
            }
          }
        }
        // 接続線ブロックごとの遅延放送パターン。空 or 各行の SSML 未入力を検出する。
        if (!Array.isArray(line.delay_patterns) || line.delay_patterns.length === 0) {
          return `${linePrefix}: 遅延放送パターンを 1 件以上設定してください`;
        }
        for (let pi = 0; pi < line.delay_patterns.length; pi++) {
          const pattern = line.delay_patterns[pi] as Partial<DelayBroadcastPattern>;
          if (
            typeof pattern.delay_minutes_from !== 'number' ||
            Number.isNaN(pattern.delay_minutes_from) ||
            pattern.delay_minutes_from < 0 ||
            !Number.isInteger(pattern.delay_minutes_from)
          ) {
            return `${linePrefix} パターン${pi + 1}: 遅れ開始分数を 0 以上の整数で指定してください`;
          }
          if (pattern.delay_minutes_to !== undefined) {
            if (
              typeof pattern.delay_minutes_to !== 'number' ||
              Number.isNaN(pattern.delay_minutes_to) ||
              !Number.isInteger(pattern.delay_minutes_to) ||
              pattern.delay_minutes_to <= pattern.delay_minutes_from
            ) {
              return `${linePrefix} パターン${pi + 1}: 遅れ終了分数は開始分数より大きい整数で指定してください`;
            }
          }
          // 方面部分文の SSML は必須 (放送文の主要素材)。
          if (typeof pattern.ssml_template !== 'string' || pattern.ssml_template.trim() === '') {
            return `${linePrefix} パターン${pi + 1}: 放送パターン (SSMLテンプレート) を入力してください`;
          }
        }
        // 接続線ブロック SSML テンプレートも必須 (未入力なら Lambda 側デフォルトに頼らせず、
        // フォームで補完済みのはず。念のため空文字を弾く)。
        if (typeof line.ssml_line_template !== 'string' || line.ssml_line_template.trim() === '') {
          return `${linePrefix}: 接続線ブロックの SSML テンプレートを入力してください`;
        }
      }
      // 冒頭 SSML テンプレートは必須 (backfill でデフォルトが入るはず。念のため空文字を弾く)。
      if (
        typeof props.ssml_intro_template !== 'string' ||
        props.ssml_intro_template.trim() === ''
      ) {
        return `${prefix} (接続線遅延／乗換駅別): 冒頭 SSML テンプレートを入力してください`;
      }
      return null;
    }

    case 'free_text': {
      const props = content.properties as Partial<FreeTextProperties>;
      const hasText = typeof props.text === 'string' && props.text.trim() !== '';
      const hasSsml = typeof props.ssml === 'string' && props.ssml.trim() !== '';
      const hasAudio = typeof props.audio_key === 'string' && props.audio_key.trim() !== '';
      // 通常テキスト / SSML / 音声ファイル (audio_key) のいずれかがあれば良い。
      if (!hasText && !hasSsml && !hasAudio) {
        return `${prefix} (任意テキスト): テキストか SSML を入力するか、音声ファイルをアップロードしてください`;
      }
      // 放送条件 (任意・複数可) のチェック。
      // - 未指定 / 空配列は「無条件放送」として許可する (後方互換)。
      // - 指定されている場合は各条件について接続線遅延と同等の必須項目チェックを行う。
      if (Array.isArray(props.conditions) && props.conditions.length > 0) {
        for (let ci = 0; ci < props.conditions.length; ci++) {
          const cond = props.conditions[ci] as Partial<BroadcastCondition> | undefined;
          const condErr = validateBroadcastCondition(
            cond,
            `${prefix} (任意テキスト) 条件${ci + 1}`,
          );
          if (condErr) return condErr;
        }
      }
      return null;
    }

    default:
      return `${prefix}: 未対応のカテゴリ ${(content as { category: string }).category}`;
  }
}

/**
 * 任意テキスト放送 (free_text) の放送条件 1 件をバリデーションする。
 *
 * 接続線遅延コンテンツと同じ 3 モード (whole_line / section_scheduled / transfer_aware) を
 * サポートし、各モードの必須項目 (route_id / 区間駅 / 乗換駅・乗換時間) を確認する。
 *
 * `delay_minutes` (比較対象の遅延分数) は 0 以上の整数、`operator` は 'gte' / 'lte' のみ許可。
 *
 * @param cond 条件オブジェクト (undefined の場合はエラー扱い)
 * @param prefix エラーメッセージの接頭辞 (例: 「コンテンツ #1 (任意テキスト) 条件1」)
 * @returns バリデーションエラー文字列 (問題なしなら null)
 */
export function validateBroadcastCondition(
  cond: Partial<BroadcastCondition> | undefined,
  prefix: string,
): string | null {
  if (!cond || typeof cond !== 'object') {
    return `${prefix}: 条件の設定が不正です`;
  }
  if (typeof cond.route_id !== 'string' || cond.route_id.trim() === '') {
    return `${prefix}: 接続線 (route_id) を指定してください`;
  }
  if (cond.operator !== 'gte' && cond.operator !== 'lte') {
    return `${prefix}: 比較演算子 (以上 / 以下) を指定してください`;
  }
  if (
    typeof cond.delay_minutes !== 'number' ||
    Number.isNaN(cond.delay_minutes) ||
    cond.delay_minutes < 0 ||
    !Number.isInteger(cond.delay_minutes)
  ) {
    return `${prefix}: 遅延分数を 0 以上の整数で指定してください`;
  }
  const mode = cond.delay_judgement_mode ?? 'whole_line';
  if (mode === 'section_scheduled') {
    const hasStart = typeof cond.start_stop_id === 'string' && cond.start_stop_id.trim() !== '';
    const hasEnd = typeof cond.end_stop_id === 'string' && cond.end_stop_id.trim() !== '';
    if (!hasStart || !hasEnd) {
      return `${prefix}: 「区間・在線便の遅延」モードでは遅延判定開始駅と終了駅を指定してください`;
    }
  }
  if (mode === 'transfer_aware') {
    const hasTransferStop =
      typeof cond.transfer_stop_id === 'string' && cond.transfer_stop_id.trim() !== '';
    if (!hasTransferStop) {
      return `${prefix}: 「乗り換え列車の遅延」モードでは乗換駅を指定してください`;
    }
    if (
      typeof cond.transfer_time_seconds !== 'number' ||
      Number.isNaN(cond.transfer_time_seconds) ||
      cond.transfer_time_seconds < 0 ||
      !Number.isInteger(cond.transfer_time_seconds)
    ) {
      return `${prefix}: 「乗り換え列車の遅延」モードでは乗換時間 (秒) を 0 以上の整数で指定してください`;
    }
  }
  return null;
}

/**
 * 任意テキスト放送 (free_text) の放送条件の既定値を生成する。
 * 「＋条件を追加」ボタンで新規条件を追加する際の初期値として使う。
 *
 * デフォルトは「接続線全体の遅延で 5 分以上」とし、ユーザは route_id を必ず選び直す。
 */
export function createDefaultBroadcastCondition(): BroadcastCondition {
  return {
    route_id: '',
    delay_judgement_mode: 'whole_line',
    delay_minutes: 5,
    operator: 'gte',
  };
}

/** 配列内の要素を index 単位で並び替えるヘルパー (immutable) */
export function moveContent(
  contents: BroadcastContent[],
  fromIndex: number,
  toIndex: number,
): BroadcastContent[] {
  if (fromIndex === toIndex) return contents;
  if (fromIndex < 0 || fromIndex >= contents.length) return contents;
  if (toIndex < 0 || toIndex >= contents.length) return contents;
  const next = contents.slice();
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return contents;
  next.splice(toIndex, 0, moved);
  return next;
}
